import { FetchUserAttributesOutput } from "aws-amplify/auth";
import { AlertCircle } from "lucide-react";

import { useGetAuthUser } from "@/api/get-user";
import { LoadingScreen } from "@/components/loading-screen";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useGetMfa } from "@/features/account-settings/api/get-mfa";
import { BasicUserInformationForm } from "@/features/account-settings/components/basic-user-information.form";
import { MfaSettingsForm } from "@/features/account-settings/components/mfa-settings.form";
import { UserAvatarForm } from "@/features/account-settings/components/user-avatar.form";
import { useGetUser } from "@/features/users/api/get-user";

export default function AccountSettingsPage() {
  const { data, isLoading, isSuccess, isError, error } = useGetAuthUser();
  const {
    data: user,
    isLoading: isUserLoading,
    isSuccess: isUserSuccess,
    isError: isUserError,
    error: userError,
  } = useGetUser(data?.sub ?? "", {
    enabled: !!data?.sub,
  });

  const {
    data: mfaData,
    isLoading: mfaLoading,
    isError: isMfaError,
    error: mfaError,
    isSuccess: isMfaSuccess,
  } = useGetMfa();

  if (isLoading || mfaLoading || isUserLoading) {
    return <LoadingScreen />;
  }

  if (
    isError ||
    !isSuccess ||
    isMfaError ||
    !isMfaSuccess ||
    isUserError ||
    !isUserSuccess
  ) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          {(error || mfaError || userError)?.message ??
            "An unexpected error occurred"}
        </AlertDescription>
      </Alert>
    );
  }

  // Types coming from the API are not compatible with the format Amplify expects
  const userData: FetchUserAttributesOutput & { timeZone?: string } = {
    avatarUrl: data.avatarUrl ?? undefined,
    given_name: data.given_name ?? undefined,
    family_name: data.family_name ?? undefined,
    email: data.email ?? undefined,
    phone_number: data.phone_number ?? undefined,
    sub: data.sub ?? undefined,
    timeZone: user.timeZone ?? undefined,
  };

  return (
    <section className="space-y-4">
      <BasicUserInformationForm
        initialData={userData}
        isEditingOwnUser={true}
      />
      <UserAvatarForm avatarURL={data.avatarUrl} userId={data.sub ?? ""} />
      <MfaSettingsForm
        initialData={{
          ...mfaData,
          phone_number: data.phone_number,
          email: data.email,
        }}
      />
    </section>
  );
}
